/**
 * Enhanced JoyBoy runtime with source registry integration
 */

import { SourceRegistry } from './registry';
import { GitHubSourceLoader } from './github-loader';
import type { Source } from './base-source';
import type { Manga } from '@joyboy-parser/types';
import type { ProgressCallback } from './github-loader';

// Import from source-registry
import {
  type RegistrySource,
  SourceCatalog,
  getAllSources,
  getSourceById,
  searchSources as searchRegistry,
  RemoteRegistry,
  DEFAULT_REGISTRY_URLS
} from '@joyboy-parser/source-registry';

/**
 * Main JoyBoy runtime class with integrated source registry
 */
export class JoyBoy {
  private static registry = SourceRegistry.getInstance();
  private static loader = new GitHubSourceLoader();
  private static remoteCatalog: SourceCatalog | null = null;
  private static remoteRegistry: RemoteRegistry | null = null;

  // ============================================================================
  // CONFIGURATION
  // ============================================================================

  /**
   * Configure remote registry
   * 
   * @param registryUrl - URL to registry JSON (defaults to jsDelivr CDN)
   * 
   * @example
   * ```typescript
   * JoyBoy.configureRegistry(
   *   'https://cdn.jsdelivr.net/gh/user/repo@main/registry/sources.json'
   * );
   * ```
   */
  static configureRegistry(registryUrl?: string): void {
    const url = registryUrl || DEFAULT_REGISTRY_URLS.jsdelivr;
    this.remoteCatalog = new SourceCatalog(url);
    this.remoteRegistry = new RemoteRegistry({ registryUrl: url });
  }

  // ============================================================================
  // REGISTRY OPERATIONS
  // ============================================================================

  /**
   * Sync with remote registry
   * 
   * @returns Promise that resolves when sync is complete
   * 
   * @example
   * ```typescript
   * await JoyBoy.syncRegistry();
   * const sources = JoyBoy.browseSources();
   * ```
   */
  static async syncRegistry(): Promise<void> {
    if (!this.remoteCatalog) {
      this.configureRegistry();
    }
    await this.remoteCatalog!.syncWithRemote();
  }

  /**
   * Browse all available sources in the registry
   * 
   * @returns Array of all source registry entries
   * 
   * @example
   * ```typescript
   * const sources = JoyBoy.browseSources();
   * sources.forEach(s => console.log(s.name));
   * ```
   */
  static browseSources(): RegistrySource[] {
    if (this.remoteCatalog) {
      return this.remoteCatalog.getAllSources();
    }
    return getAllSources();
  }

  /**
   * Search for sources in the registry
   * 
   * @param query - Search query (name, id, description, tags)
   * @returns Matching registry entries
   * 
   * @example
   * ```typescript
   * const mangaSources = JoyBoy.searchSources('manga');
   * const apiSources = JoyBoy.searchSources('api');
   * ```
   */
  static searchSources(query: string): RegistrySource[] {
    if (this.remoteCatalog) {
      return this.remoteCatalog.searchSources(query);
    }
    return searchRegistry(query);
  }

  /**
   * Get source registry information
   * 
   * @param sourceId - Source identifier
   * @returns Registry entry or undefined if not found
   */
  static getSourceInfo(sourceId: string): RegistrySource | undefined {
    if (this.remoteCatalog) {
      return this.remoteCatalog.getSource(sourceId);
    }
    return getSourceById(sourceId);
  }

  /**
   * Get information about installed sources
   * 
   * @returns Array of installed source registry entries
   */
  static getInstalledSourcesInfo(): RegistrySource[] {
    const installed = this.registry.list().map(s => s.id);
    return this.browseSources().filter(s => installed.includes(s.id));
  }

  /**
   * Get information about available (not installed) sources
   * 
   * @returns Array of available source registry entries
   */
  static getAvailableSourcesInfo(): RegistrySource[] {
    const installed = this.registry.list().map(s => s.id);
    return this.browseSources().filter(s => !installed.includes(s.id));
  }

  // ============================================================================
  // SOURCE INSTALLATION & MANAGEMENT
  // ============================================================================

  /**
   * Install source from registry
   * 
   * @param sourceId - Source identifier from registry
   * @param onProgress - Optional progress callback
   * @returns Loaded source instance
   * 
   * @example
   * ```typescript
   * const source = await JoyBoy.installSource('mangadex', (progress, status) => {
   *   console.log(`${progress}%: ${status}`);
   * });
   * ```
   */
  static async installSource(
    sourceId: string,
    onProgress?: ProgressCallback
  ): Promise<Source> {
    const entry = this.getSourceInfo(sourceId);

    if (!entry) {
      throw new Error(`Source not found in registry: ${sourceId}`);
    }

    try {
      // Load source from the download URL
      const source = await this.loader.loadFromRegistry(entry, onProgress);

      // Register in local registry
      this.registry.register(source);

      return source;
    } catch (error) {
      throw new Error(`Installation failed: ${(error as Error).message}`);
    }
  }

  /**
   * Uninstall a source
   * 
   * @param sourceId - Source identifier
   * 
   * @example
   * ```typescript
   * JoyBoy.uninstallSource('mangadex');
   * ```
   */
  static uninstallSource(sourceId: string): void {
    // Remove from local registry
    this.registry.unregister(sourceId);

    // Clear cache
    this.loader.clearCache(sourceId);
  }

  /**
   * Reinstall/update a source
   * 
   * @param sourceId - Source identifier
   * @param onProgress - Optional progress callback
   * @returns Updated source instance
   * 
   * @example
   * ```typescript
   * const source = await JoyBoy.updateSource('mangadex');
   * ```
   */
  static async updateSource(
    sourceId: string,
    onProgress?: ProgressCallback
  ): Promise<Source> {
    // Clear cache first
    this.loader.clearCache(sourceId);

    // Uninstall current version
    this.uninstallSource(sourceId);

    // Install new version
    return this.installSource(sourceId, onProgress);
  }

  /**
   * Check for available updates
   * 
   * @returns Array of sources with updates available
   * 
   * @example
   * ```typescript
   * const updates = await JoyBoy.checkForUpdates();
   * updates.forEach(source => {
   *   console.log(`${source.name}: Update available to ${source.version}`);
   * });
   * ```
   */
  static async checkForUpdates(): Promise<RegistrySource[]> {
    // Sync registry first
    await this.syncRegistry();

    // Get installed sources with their current info
    const installed = this.registry.list();
    const installedVersions = new Map(
      installed.map(s => [s.id, s.version])
    );

    // Get latest versions from registry
    const available = this.browseSources();
    
    // Find sources with newer versions
    return available.filter(source => {
      const installedVersion = installedVersions.get(source.id);
      if (!installedVersion) return false;
      
      // Simple version comparison (can be enhanced with semver)
      return source.version !== installedVersion;
    });
  }

  /**
   * Install all available updates
   * 
   * @param onProgress - Optional progress callback (receives sourceId and progress)
   * 
   * @example
   * ```typescript
   * await JoyBoy.updateAll((sourceId, progress, status) => {
   *   console.log(`${sourceId}: ${progress}% - ${status}`);
   * });
   * ```
   */
  static async updateAll(
    onProgress?: (sourceId: string, progress: number, status: string) => void
  ): Promise<void> {
    const updates = await this.checkForUpdates();

    for (const update of updates) {
      try {
        await this.updateSource(update.id, (progress, status) => {
          onProgress?.(update.id, progress, status);
        });
      } catch (error) {
        console.error(`Failed to update ${update.id}:`, error);
      }
    }
  }

  // ============================================================================
  // DIRECT SOURCE LOADING (Existing Methods)
  // ============================================================================

  /**
   * Load source directly (for development/testing)
   * 
   * @param source - Source instance, package name, or lazy loader
   * @returns Loaded source instance
   * 
   * @example
   * ```typescript
   * // From instance
   * await JoyBoy.loadSource(new MangaDexSource());
   * 
   * // From package (if published to NPM)
   * await JoyBoy.loadSource('@joyboy/source-mangadex');
   * 
   * // From lazy loader
   * await JoyBoy.loadSource(() => import('@joyboy/source-mangadex'));
   * ```
   */
  static async loadSource(
    source: Source | string | (() => Promise<any>)
  ): Promise<Source> {
    try {
      let sourceInstance: Source;

      if (typeof source === 'string') {
        // Dynamic import from package name
        const module = await import(/* @vite-ignore */ source);
        const SourceClass = module.default;

        if (!SourceClass) {
          throw new Error(`No default export found in ${source}`);
        }

        sourceInstance = new SourceClass();
      } else if (typeof source === 'function') {
        // Lazy loader function
        const module = await source();
        const SourceClass = module.default;
        sourceInstance = new SourceClass();
      } else {
        // Direct instance
        sourceInstance = source;
      }

      // Validate and register
      this.validateSource(sourceInstance);
      this.registry.register(sourceInstance);

      return sourceInstance;
    } catch (error) {
      throw new Error(`Failed to load source: ${(error as Error).message}`);
    }
  }

  // ============================================================================
  // SOURCE ACCESS
  // ============================================================================

  /**
   * Get a loaded source by ID
   * 
   * @param id - Source identifier
   * @returns Source instance
   * @throws Error if source is not loaded
   * 
   * @example
   * ```typescript
   * const mangadex = JoyBoy.getSource('mangadex');
   * const results = await mangadex.search('One Piece');
   * ```
   */
  static getSource(id: string): Source {
    const source = this.registry.get(id);

    if (!source) {
      const available = this.registry.list().map(s => s.id).join(', ');
      throw new Error(
        `Source '${id}' is not loaded. Available sources: ${available || 'none'}`
      );
    }

    return source;
  }

  /**
   * List all loaded sources
   * 
   * @returns Array of loaded source instances
   * 
   * @example
   * ```typescript
   * const sources = JoyBoy.listSources();
   * sources.forEach(source => {
   *   console.log(`${source.name} (${source.id})`);
   * });
   * ```
   */
  static listSources(): Source[] {
    return this.registry.list();
  }

  /**
   * Check if a source is loaded
   * 
   * @param id - Source identifier
   * @returns True if loaded
   * 
   * @example
   * ```typescript
   * if (JoyBoy.hasSource('mangadex')) {
   *   const source = JoyBoy.getSource('mangadex');
   * }
   * ```
   */
  static hasSource(id: string): boolean {
    return this.registry.has(id);
  }

  /**
   * Unload a source (without uninstalling)
   * 
   * @param id - Source identifier
   * @returns True if unloaded
   */
  static unloadSource(id: string): boolean {
    return this.registry.unregister(id);
  }

  /**
   * Clear all loaded sources
   */
  static clearSources(): void {
    this.registry.clear();
  }

  // ============================================================================
  // MULTI-SOURCE OPERATIONS
  // ============================================================================

  /**
   * Search across multiple sources
   * 
   * @param query - Search query
   * @param sourceIds - Optional array of source IDs (default: all loaded)
   * @returns Map of source ID to results
   * 
   * @example
   * ```typescript
   * const results = await JoyBoy.searchAll('One Piece');
   * 
   * for (const [sourceId, manga] of results) {
   *   console.log(`${sourceId}: ${manga.length} results`);
   * }
   * ```
   */
  static async searchAll(
    query: string,
    sourceIds?: string[]
  ): Promise<Map<string, Manga[]>> {
    const sources = sourceIds
      ? sourceIds.map(id => this.getSource(id))
      : this.registry.list().filter(s => s.search);

    const results = await Promise.allSettled(
      sources.map(async source => {
        if (source.search) {
          const items = await source.search(query);
          return { sourceId: source.id, items };
        }
        return { sourceId: source.id, items: [] };
      })
    );

    const resultMap = new Map<string, Manga[]>();

    results.forEach(result => {
      if (result.status === 'fulfilled') {
        resultMap.set(result.value.sourceId, result.value.items);
      }
    });

    return resultMap;
  }

  // ============================================================================
  // VALIDATION
  // ============================================================================

  /**
   * Validate source instance
   */
  private static validateSource(source: any): asserts source is Source {
    const required = ['id', 'name', 'version', 'baseUrl', 'getMangaDetails', 'getChapters', 'getChapterPages'];

    for (const prop of required) {
      if (!(prop in source)) {
        throw new Error(`Source validation failed: missing required property '${prop}'`);
      }
    }

    if (typeof source.getMangaDetails !== 'function') {
      throw new Error('Source validation failed: getMangaDetails must be a function');
    }

    if (typeof source.getChapters !== 'function') {
      throw new Error('Source validation failed: getChapters must be a function');
    }

    if (typeof source.getChapterPages !== 'function') {
      throw new Error('Source validation failed: getChapterPages must be a function');
    }
  }
}
